/**
 * Billing (plan §13.4, §15.1, §15.3).
 *
 * §15.1 is the load-bearing rule on this screen: CANCELLING IS NOT DELETING, and the difference
 * must be stated BEFORE the customer is sent into the payment portal or the delete flow. So the
 * two actions live in visually distinct cards with explicit consequences, and deletion is a
 * two-step confirmation that lives on the data screen where the export sits next to it.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "../api/client.js";
import { Banner, ErrorNote, Modal, StatusPill, formatDay } from "../components/ui.js";
import { useSession } from "../state/session.js";

const REASON_COPY: Record<string, string> = {
  active: "Your subscription is active.",
  trialing: "You are on a trial.",
  grace_period: "A payment failed. Everything still works while you sort it out.",
  no_subscription: "You do not have an active subscription.",
  past_due_expired: "A payment failed and the grace period has passed.",
  unpaid: "An invoice has not been paid.",
  canceled: "Your subscription has been cancelled.",
  paused: "Your subscription is paused.",
};

export default function Billing() {
  const { me, refresh } = useSession();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [reason, setReason] = useState("");
  const [notice, setNotice] = useState<string | null>(null);

  if (!me) return null;
  const { entitlement, deployment } = me;

  async function openPortal() {
    setBusy("portal");
    setError(null);
    try {
      const { url } = await api.portal(`${window.location.origin}/billing`);
      window.location.href = url;
    } catch (e) {
      setError(e);
      setBusy(null);
    }
  }

  async function cancelService() {
    setBusy("cancel");
    setError(null);
    try {
      const res = await api.cancelService(reason.trim() || undefined);
      setConfirmCancel(false);
      // Say what actually happened: billing stops on a date, and the Construct keeps running
      // until then. The status pill will not change today, so the copy has to carry it.
      setNotice(
        res.serviceEndsAt
          ? `Cancelled. Your Construct keeps running until ${formatDay(res.serviceEndsAt)}, and your data is kept for ${res.retentionDays} days after that.`
          : "Cancelled. Your data is kept so you can export it or come back.",
      );
      await refresh();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  /**
   * Reactivation has three shapes (API-CONTRACT §7). Two of them hand back a URL we must send
   * the customer to; only `resubscribed` finishes here, and even then the deployment comes back
   * on via the billing webhook, so we refresh rather than assume a status.
   */
  async function reactivate() {
    setBusy("reactivate");
    setError(null);
    try {
      const res = await api.reactivate(`${window.location.origin}/billing`);
      if (res.outcome === "checkout_required" && res.checkoutUrl) {
        window.location.href = res.checkoutUrl;
        return;
      }
      if (res.outcome === "billing_action_required" && res.portalUrl) {
        window.location.href = res.portalUrl;
        return;
      }
      setNotice(res.message);
      await refresh();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  const notImplemented = error instanceof ApiError && error.code === "not_implemented";
  // The API decides what is legal (src/control/lifecycle.ts); these fallbacks only cover an
  // older API build that predates the `lifecycle` block on /api/me.
  const canReactivate =
    me.lifecycle?.canReactivate ??
    (deployment?.status === "RETAINED" ||
      deployment?.status === "SUSPENDED" ||
      deployment?.status === "CANCELLATION_PENDING");
  const canCancel =
    me.lifecycle?.canCancel ?? (deployment?.status === "READY" || deployment?.status === "SUSPENDED");

  return (
    <>
      <div className="page-head">
        <p className="eyebrow">Billing</p>
        <h1>Your subscription</h1>
      </div>

      {notImplemented ? (
        <Banner kind="warn" title="Not available yet">
          This part of billing is still being finished. Nothing has changed on your account.
        </Banner>
      ) : error ? (
        <ErrorNote error={error} />
      ) : null}
      {notice ? <Banner kind="info">{notice}</Banner> : null}

      <div className="card">
        <div className="card__head">
          <div>
            <h3>Status</h3>
            <p className="muted" style={{ marginTop: 6 }}>
              {REASON_COPY[entitlement.reason] ?? "Subscription status unavailable."}
            </p>
          </div>
          {deployment ? <StatusPill status={deployment.status} /> : null}
        </div>
        <div className="row" style={{ marginTop: 18, gap: 26 }}>
          <span className="mono small muted">
            Renews {formatDay(entitlement.currentPeriodEnd)}
          </span>
          {entitlement.cancelAtPeriodEnd ? (
            <span className="pill pill--warn">Ends at period end</span>
          ) : null}
          {entitlement.trialEnd ? (
            <span className="mono small muted">Trial ends {formatDay(entitlement.trialEnd)}</span>
          ) : null}
        </div>
        <div className="btn-row">
          <button type="button" className="btn btn--primary" disabled={busy !== null} onClick={() => void openPortal()}>
            {busy === "portal" ? "Opening" : "Manage payment details"}
          </button>
          {canReactivate ? (
            <button type="button" className="btn btn--ghost" disabled={busy !== null} onClick={() => void reactivate()}>
              {busy === "reactivate" ? "Working" : "Reactivate"}
            </button>
          ) : null}
        </div>
      </div>

      {/* ---- §15.1: the distinction, stated before either action -------------- */}
      <h2 style={{ marginTop: 44, marginBottom: 16 }}>Stopping</h2>
      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))" }}>
        <div className="card">
          <h3>Cancel your subscription</h3>
          <div className="card__body">
            <p style={{ marginTop: 0 }}>
              Billing stops at the end of the period you have already paid for. After that:
            </p>
            <ul className="small" style={{ color: "var(--chrome-2)", paddingLeft: 18 }}>
              <li>your data stays intact,</li>
              <li>you can still export everything,</li>
              <li>you can reactivate and pick up where you left off,</li>
              <li>ingest and agent access are switched off.</li>
            </ul>
            <p className="small">
              <strong>Cancelling does not delete anything.</strong>
            </p>
          </div>
          <div className="btn-row">
            <button
              type="button"
              className="btn btn--ghost"
              disabled={!canCancel || busy !== null}
              onClick={() => setConfirmCancel(true)}
            >
              Cancel subscription
            </button>
          </div>
        </div>

        <div className="card card--danger">
          <h3>Delete your Construct</h3>
          <div className="card__body">
            <p style={{ marginTop: 0 }}>
              A different thing entirely. Deleting destroys your vault, your stored files, your
              database, and the service that runs it. Permanently, with no way back.
            </p>
            <p className="small">Export first. We will ask you to confirm twice.</p>
          </div>
          <div className="btn-row">
            <Link className="btn btn--danger" to="/data">
              Go to deletion
            </Link>
          </div>
        </div>
      </div>

      {confirmCancel ? (
        <Modal title="Cancel subscription" onClose={() => setConfirmCancel(false)}>
          <p className="modal__id">Confirm</p>
          <h2>Cancel your subscription?</h2>
          <p className="muted">
            Your Construct keeps running until {formatDay(entitlement.currentPeriodEnd)}. Your data
            is kept afterwards so you can export it or come back. Nothing is deleted.
          </p>
          <label className="field">
            <span className="field__label">Anything you want to tell us? (optional)</span>
            <input
              className="input"
              type="text"
              value={reason}
              maxLength={200}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Not using it enough"
            />
          </label>
          <div className="btn-row">
            <button type="button" className="btn btn--danger" disabled={busy !== null} onClick={() => void cancelService()}>
              {busy === "cancel" ? "Cancelling" : "Cancel subscription"}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setConfirmCancel(false)}>
              Keep it
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
